import * as ImagePicker from 'expo-image-picker';
import { useFocusEffect, useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  AlarmSmoke,
  Camera,
  ChevronLeft,
  CircleAlert,
  Download,
  Droplet,
  FileText,
  Flame,
  Layers,
  MapPin,
  Shovel,
  Wind,
  X,
  Zap,
} from 'lucide-react-native';
import { ComponentType, useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import Animated, {
  Easing,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { SafeAreaView } from 'react-native-safe-area-context';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { SYSTEM_CATALOG } from '@/data/systems';
import type { ContactRow, SystemLocation, SystemRow } from '@/db/types';
import { goBackOrTo, goTo } from '@/lib/nav';
import { listContacts } from '@/lib/contacts';
import { persistPickedFile } from '@/lib/files';
import { exportEmergencyCardPdf } from '@/lib/pdf';
import {
  addSystem,
  getActiveProperty,
  getSystemsForProperty,
  updateSystem,
} from '@/lib/tasks';
import { webNoOutline } from '@/lib/web-style';

/**
 * Emergency Card — single screen for capturing AND exporting the
 * shutoff / critical-systems info that gets printed and taped inside a
 * cabinet door for the moment the basement is flooding and the phone
 * is dead.
 *
 * This used to be two screens (/locations to edit + /emergency-card to
 * preview + export) which felt redundant — same eight rows, same UX
 * vocabulary, different routes. Now it's one: each row is tap-to-edit
 * (Where + How + Photo) and the bottom button exports the PDF.
 */

interface EmergencyEntry {
  /** Catalog kind. Existing system row matches by `kind === entry.kind`. */
  kind: string;
  /** Display label override if catalog label is too long for this UI. */
  label: string;
  /** Placeholder text for the WHERE field — typical home location. */
  whereHint: string;
  /** Placeholder text for the HOW TO OPERATE field. */
  howHint: string;
  /** Default location (inside / outside) used when auto-creating the row. */
  location: SystemLocation;
  Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  tint: string;
  bg: string;
}

const ENTRIES: EmergencyEntry[] = [
  {
    kind: 'water_main_shutoff',
    label: 'Water main shutoff',
    whereHint: 'e.g. Basement utility room, north wall behind the water heater',
    howHint: 'e.g. Quarter-turn lever — straight up = closed',
    location: 'inside',
    Icon: Droplet,
    tint: '#2A6F97',
    bg: 'rgba(42,111,151,0.12)',
  },
  {
    kind: 'gas_shutoff',
    label: 'Gas main shutoff',
    whereHint: 'e.g. At the gas meter, west side of the house',
    howHint: 'e.g. Crescent wrench — quarter-turn perpendicular = closed',
    location: 'outside',
    Icon: Flame,
    tint: Palette.clay,
    bg: 'rgba(176,95,60,0.15)',
  },
  {
    kind: 'panel',
    label: 'Electrical panel (main breaker)',
    whereHint: 'e.g. Garage, next to the utility door',
    howHint: 'e.g. Main breaker is the largest at the top — flip down = off',
    location: 'inside',
    Icon: Zap,
    tint: Palette.honeyDeep,
    bg: 'rgba(225,163,61,0.18)',
  },
  {
    kind: 'hvac_condensate_line',
    label: 'HVAC disconnect + condensate',
    whereHint: 'e.g. Gray box on the wall next to the outdoor AC condenser',
    howHint: 'e.g. Pull out the disconnect to cut power. Condensate access tee inside near the air handler.',
    location: 'inside',
    Icon: Wind,
    tint: Palette.forest,
    bg: '#EAF0EA',
  },
  {
    kind: 'sump',
    label: 'Sump pump',
    whereHint: 'e.g. Lowest corner of the basement, southwest wall',
    howHint: 'e.g. Test the float annually; backup battery + alarm sit alongside',
    location: 'inside',
    Icon: AlarmSmoke,
    tint: '#2A6F97',
    bg: 'rgba(42,111,151,0.12)',
  },
  {
    kind: 'drain_cleanout',
    label: 'Main drain cleanout',
    whereHint: 'e.g. East-side foundation, behind the hydrangea bush',
    howHint: 'e.g. Plumber\'s wrench to remove the cap; black PVC ~3" diameter',
    location: 'outside',
    Icon: Shovel,
    tint: Palette.forestLight,
    bg: '#EAF0EA',
  },
  {
    kind: 'septic',
    label: 'Septic tank lid',
    whereHint: 'e.g. 18 ft from the southwest corner of the house',
    howHint: 'e.g. Concrete lid — needs lifting bar. Mark with a pin so it\'s findable.',
    location: 'outside',
    Icon: Layers,
    tint: Palette.forest,
    bg: '#EAF0EA',
  },
  {
    kind: 'wellw',
    label: 'Well cap',
    whereHint: 'e.g. 6 ft east of the basement window, 4" cap above grade',
    howHint: 'e.g. Pressure tank + pressure switch in the basement utility room',
    location: 'outside',
    Icon: Droplet,
    tint: '#2A6F97',
    bg: 'rgba(42,111,151,0.12)',
  },
];

// Legacy shape kept for the PDF call so we don't churn the lib/pdf API.
const EMERGENCY_KINDS = ENTRIES.map((e) => ({ kind: e.kind, label: e.label }));

const EMERGENCY_ROLES: { role: string; label: string }[] = [
  { role: 'plumber', label: 'Plumber' },
  { role: 'electrician', label: 'Electrician' },
  { role: 'hvac', label: 'HVAC' },
  { role: 'insurance', label: 'Insurance' },
  { role: 'hoa', label: 'HOA' },
];

export default function EmergencyCardScreen() {
  const router = useRouter();
  const db = useSQLiteContext();
  const [property, setProperty] = useState<Awaited<
    ReturnType<typeof getActiveProperty>
  > | null>(null);
  const [systems, setSystems] = useState<SystemRow[]>([]);
  const [contacts, setContacts] = useState<ContactRow[]>([]);
  const [exporting, setExporting] = useState(false);

  // Edit-modal state. Opening a row sets `active` + prefills the
  // existing system's where/how/photo so the modal acts as an "edit
  // this row" rather than a blank-slate create.
  const [active, setActive] = useState<EmergencyEntry | null>(null);
  const [activeSystem, setActiveSystem] = useState<SystemRow | null>(null);
  const [whereText, setWhereText] = useState('');
  const [howText, setHowText] = useState('');
  const [photoUri, setPhotoUri] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Backdrop fades, dialog slides — driven by ONE progress value that
  // we map separately into opacity (backdrop) and opacity + translateY
  // (dialog). The Modal uses animationType="none" so React Native
  // doesn't drag both panes up together.
  const modalAnim = useSharedValue(0);
  const backdropStyle = useAnimatedStyle(() => ({
    opacity: modalAnim.value * 0.55,
  }));
  const dialogStyle = useAnimatedStyle(() => ({
    opacity: 0.3 + modalAnim.value * 0.7,
    transform: [{ translateY: (1 - modalAnim.value) * 120 }],
  }));

  // Drive the open animation each time `active` flips to truthy.
  useEffect(() => {
    if (active) {
      modalAnim.value = withTiming(1, {
        duration: 280,
        easing: Easing.out(Easing.cubic),
      });
    }
  }, [active, modalAnim]);

  const reload = useCallback(async () => {
    const p = await getActiveProperty(db);
    if (!p) {
      setProperty(null);
      return;
    }
    const [allSystems, allContacts] = await Promise.all([
      getSystemsForProperty(db, p.id),
      listContacts(db, p.id),
    ]);
    setProperty(p);
    setSystems(allSystems);
    setContacts(allContacts);
  }, [db]);

  useFocusEffect(
    useCallback(() => {
      void reload();
    }, [reload])
  );

  const findSystem = (kind: string) =>
    systems.find((s) => s.kind === kind && !s.deleted_at) ?? null;
  const findContact = (role: string) =>
    contacts.find(
      (c) => c.role && c.role.toLowerCase() === role && !c.deleted_at
    ) ?? null;

  const filledLocations = EMERGENCY_KINDS.filter((e) => {
    const s = findSystem(e.kind);
    return !!(s?.location_photo_uri || s?.location_where || s?.location_note);
  }).length;
  const filledContacts = EMERGENCY_ROLES.filter((r) =>
    findContact(r.role)
  ).length;

  const onExport = async () => {
    if (!property || exporting) return;
    setExporting(true);
    try {
      await exportEmergencyCardPdf({ property, systems, contacts });
    } finally {
      setExporting(false);
    }
  };

  /** Open the edit modal for a row. Prefills from the existing system
   *  row if one exists; otherwise starts blank (we'll create the row on
   *  Save). */
  const openEntry = (entry: EmergencyEntry) => {
    const existing = systems.find(
      (s) => s.kind === entry.kind && !s.deleted_at
    ) ?? null;
    setActive(entry);
    setActiveSystem(existing);
    setWhereText(existing?.location_where ?? '');
    setHowText(existing?.location_note ?? '');
    setPhotoUri(existing?.location_photo_uri ?? null);
  };

  /** Snap state back to the initial closed shape. Called by the close
   *  animation's worklet callback after the exit timing finishes — so
   *  the dialog stays visible during the slide-down animation rather
   *  than disappearing on first click. */
  const resetModalState = useCallback(() => {
    setActive(null);
    setActiveSystem(null);
    setWhereText('');
    setHowText('');
    setPhotoUri(null);
  }, []);

  const closeEdit = useCallback(() => {
    modalAnim.value = withTiming(
      0,
      { duration: 220, easing: Easing.in(Easing.cubic) },
      (finished) => {
        if (finished) runOnJS(resetModalState)();
      }
    );
  }, [modalAnim, resetModalState]);

  /** Photo picker → persist into the app's document directory so the
   *  URI survives across launches. The system row stores the on-disk
   *  URI on location_photo_uri. */
  const pickPhoto = async () => {
    if (!active) return;
    const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!perm.granted) return;
    const res = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 0.7,
    });
    if (res.canceled || !res.assets[0]) return;
    setSaving(true);
    try {
      const persisted = await persistPickedFile(
        res.assets[0].uri,
        res.assets[0].fileName ?? `location-${active.kind}-${Date.now()}.jpg`
      );
      setPhotoUri(persisted);
    } finally {
      setSaving(false);
    }
  };

  const clearPhoto = () => setPhotoUri(null);

  /** Save the edited row. If no system row exists yet for this kind, we
   *  create a placeholder so the where/how/photo have somewhere to live;
   *  the location fields then get set via a follow-up updateSystem call
   *  (NewSystemInput intentionally stays narrow). */
  const saveEdit = async () => {
    if (!active || !property) return;
    setSaving(true);
    try {
      let systemId = activeSystem?.id;
      if (!systemId) {
        const meta = SYSTEM_CATALOG[active.kind];
        systemId = await addSystem(db, {
          propertyId: property.id,
          kind: active.kind,
          location: meta?.location ?? active.location,
        });
      }
      await updateSystem(db, systemId, {
        location_photo_uri: photoUri,
        location_where: whereText.trim() || null,
        location_note: howText.trim() || null,
      });
      await reload();
      closeEdit();
    } finally {
      setSaving(false);
    }
  };

  if (!property) {
    return (
      <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
        <View style={styles.empty}>
          <Text style={styles.emptyText}>No property yet.</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable
          onPress={() => goBackOrTo(router, '/(tabs)/my-home')}
          style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]}
          hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>Emergency Card</Text>
        <View style={{ width: 36 }} />
      </View>

      <ScrollView contentContainerStyle={{ paddingBottom: 100 }}>
        <Text style={styles.h1}>One page. Tape it inside a cabinet.</Text>
        <Text style={styles.sub}>
          The information you need when the basement is flooding and the
          phone is dead. Print it; update it once a year.
        </Text>

        <View style={styles.progressCard}>
          <View style={styles.progressStat}>
            <Text style={styles.progressNum}>
              {filledLocations}/{EMERGENCY_KINDS.length}
            </Text>
            <Text style={styles.progressLabel}>Locations</Text>
          </View>
          <View style={styles.progressDivider} />
          <View style={styles.progressStat}>
            <Text style={styles.progressNum}>
              {filledContacts}/{EMERGENCY_ROLES.length}
            </Text>
            <Text style={styles.progressLabel}>Contacts</Text>
          </View>
          <View style={{ flex: 1 }} />
          <View style={styles.editHint}>
            <MapPin size={11} color={Palette.inkFaint} strokeWidth={2} />
            <Text style={styles.editHintText}>Tap a row to edit</Text>
          </View>
        </View>

        <Text style={styles.sectionTitle}>SHUTOFFS &amp; CRITICAL</Text>
        <View style={styles.list}>
          {ENTRIES.slice(0, 4).map((e) => {
            const s = findSystem(e.kind);
            return (
              <PreviewRow
                key={e.kind}
                entry={e}
                system={s}
                onPress={() => openEntry(e)}
              />
            );
          })}
        </View>

        <Text style={styles.sectionTitle}>OTHER LOCATIONS</Text>
        <View style={styles.list}>
          {ENTRIES.slice(4).map((e) => {
            const s = findSystem(e.kind);
            return (
              <PreviewRow
                key={e.kind}
                entry={e}
                system={s}
                compact
                onPress={() => openEntry(e)}
              />
            );
          })}
        </View>

        <Text style={styles.sectionTitle}>CRITICAL CONTACTS</Text>
        <View style={styles.list}>
          {EMERGENCY_ROLES.map((r) => {
            const c = findContact(r.role);
            return (
              <View key={r.role} style={styles.contactRow}>
                <Text style={styles.contactRole}>{r.label}</Text>
                <View style={{ flex: 1 }}>
                  <Text style={styles.contactName}>{c?.name ?? '—'}</Text>
                  <Text style={styles.contactPhone}>{c?.phone ?? '—'}</Text>
                </View>
              </View>
            );
          })}
          <Pressable
            onPress={() => goTo(router, '/contacts')}
            style={({ pressed }) => [styles.editBtn, pressed && { opacity: 0.9 }]}>
            <CircleAlert size={13} color={Palette.forest} strokeWidth={2} />
            <Text style={styles.editBtnText}>Manage contacts</Text>
          </Pressable>
        </View>
      </ScrollView>

      <View style={styles.footer}>
        <Pressable
          onPress={onExport}
          disabled={exporting}
          style={({ pressed }) => [
            styles.exportBtn,
            pressed && { opacity: 0.9 },
            exporting && { opacity: 0.7 },
          ]}>
          {exporting ? (
            <ActivityIndicator color={Palette.cream} size="small" />
          ) : (
            <>
              <Download size={16} color={Palette.cream} strokeWidth={2.2} />
              <Text style={styles.exportBtnText}>
                Export Emergency Card PDF
              </Text>
            </>
          )}
        </Pressable>
      </View>

      {/*
        Edit sheet — opens over the list when a row is tapped. Captures
        a photo, Where it lives, and How to operate. Same shape as the
        old /locations modal, lifted in here so editing and exporting
        live on one screen.
      */}
      <Modal
        visible={!!active}
        animationType="none"
        transparent
        onRequestClose={closeEdit}>
        {/*
          Two animated layers:
            - backdropStyle drives just the opacity of the dim overlay
              (0 → 0.55) — a clean fade in/out.
            - dialogStyle drives the sheet's translateY + opacity, so the
              card itself still slides up from the bottom while the
              backdrop fades. Slide-only feels jarring; fade-only loses
              the "sheet" affordance — this hybrid is the calm middle.
        */}
        <Animated.View style={[styles.modalBackdrop, backdropStyle]}>
          <Pressable style={StyleSheet.absoluteFill} onPress={closeEdit} />
        </Animated.View>
        <View style={styles.modalCenter} pointerEvents="box-none">
          <Animated.View style={[styles.modalCard, dialogStyle]}>
            <View
              onStartShouldSetResponder={() => true}
              onResponderRelease={(e) => e.stopPropagation()}>
            <Text style={styles.modalTitle}>{active?.label}</Text>
            <Text style={styles.modalHint}>
              Where it lives, a photo, and one sentence on how to operate it.
            </Text>

            <View style={styles.photoPickerWrap}>
              <Pressable
                onPress={pickPhoto}
                disabled={saving}
                style={({ pressed }) => [
                  styles.photoPicker,
                  pressed && { opacity: 0.9 },
                ]}>
                {photoUri ? (
                  <Image source={{ uri: photoUri }} style={styles.photoPreview} />
                ) : (
                  <View style={styles.photoEmptyPicker}>
                    <Camera size={24} color={Palette.inkSoft} strokeWidth={1.6} />
                    <Text style={styles.photoEmptyText}>Tap to add photo</Text>
                  </View>
                )}
              </Pressable>
              {photoUri ? (
                <>
                  <Pressable
                    onPress={clearPhoto}
                    hitSlop={10}
                    accessibilityLabel="Remove photo"
                    style={({ pressed }) => [
                      styles.photoClearBtn,
                      pressed && { opacity: 0.85 },
                    ]}>
                    <X size={16} color={Palette.cream} strokeWidth={2.4} />
                  </Pressable>
                  <Pressable
                    onPress={pickPhoto}
                    disabled={saving}
                    style={({ pressed }) => [
                      styles.photoReplaceBtn,
                      pressed && { opacity: 0.9 },
                    ]}>
                    <Camera size={12} color={Palette.cream} strokeWidth={2.2} />
                    <Text style={styles.photoReplaceText}>Replace</Text>
                  </Pressable>
                </>
              ) : null}
            </View>

            <View style={styles.fieldLabelRow}>
              <MapPin size={12} color={Palette.inkFaint} strokeWidth={2} />
              <Text style={[styles.fieldLabel, { marginTop: 0 }]}>WHERE IT IS</Text>
            </View>
            <TextInput
              value={whereText}
              onChangeText={setWhereText}
              placeholder={active?.whereHint ?? ''}
              placeholderTextColor={Palette.inkFaint}
              multiline
              numberOfLines={2}
              style={[styles.modalInput, webNoOutline]}
            />

            <Text style={styles.fieldLabel}>HOW TO OPERATE</Text>
            <TextInput
              value={howText}
              onChangeText={setHowText}
              placeholder={active?.howHint ?? ''}
              placeholderTextColor={Palette.inkFaint}
              multiline
              numberOfLines={2}
              style={[styles.modalInput, webNoOutline]}
            />

            <View style={styles.modalBtnRow}>
              <Pressable
                onPress={closeEdit}
                disabled={saving}
                style={({ pressed }) => [
                  styles.cancelBtn,
                  pressed && { opacity: 0.85 },
                ]}>
                <Text style={styles.cancelBtnText}>Cancel</Text>
              </Pressable>
              <Pressable
                onPress={saveEdit}
                disabled={saving}
                style={({ pressed }) => [
                  styles.saveBtn,
                  pressed && { opacity: 0.9 },
                  saving && { opacity: 0.7 },
                ]}>
                <Text style={styles.saveBtnText}>{saving ? 'Saving…' : 'Save'}</Text>
              </Pressable>
            </View>
            </View>
          </Animated.View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

function PreviewRow({
  entry,
  system,
  compact = false,
  onPress,
}: {
  entry: EmergencyEntry;
  system: SystemRow | null;
  compact?: boolean;
  onPress: () => void;
}) {
  const photo = system?.location_photo_uri ?? null;
  const where = system?.location_where ?? null;
  const note = system?.location_note ?? null;
  const hasAny = !!(photo || where || note);
  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [
        styles.row,
        compact && styles.rowCompact,
        hasAny && styles.rowFilled,
        pressed && { opacity: 0.9 },
      ]}>
      {!compact ? (
        photo ? (
          <Image source={{ uri: photo }} style={styles.photo} />
        ) : (
          <View style={[styles.photo, styles.photoEmpty, { backgroundColor: entry.bg }]}>
            <entry.Icon size={20} color={entry.tint} strokeWidth={1.7} />
          </View>
        )
      ) : (
        <View style={[styles.iconBubble, { backgroundColor: entry.bg }]}>
          <entry.Icon size={16} color={entry.tint} strokeWidth={1.7} />
        </View>
      )}
      <View style={{ flex: 1 }}>
        <Text style={styles.rowLabel}>{entry.label}</Text>
        {where ? (
          <Text style={styles.rowWhere} numberOfLines={2}>
            <Text style={styles.rowFieldLabel}>Where: </Text>
            {where}
          </Text>
        ) : null}
        {note ? (
          <Text style={styles.rowNote} numberOfLines={2}>
            <Text style={styles.rowFieldLabel}>How: </Text>
            {note}
          </Text>
        ) : null}
        {!hasAny ? (
          <Text style={[styles.rowNote, styles.rowNoteEmpty]} numberOfLines={1}>
            Tap to capture location, photo, and how to operate
          </Text>
        ) : null}
      </View>
      {!hasAny ? (
        <FileText size={14} color={Palette.inkFaint} strokeWidth={1.6} />
      ) : null}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  empty: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: { fontFamily: Fonts.sansMedium, color: Palette.inkSoft },

  topbar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.four,
    paddingTop: Spacing.two,
    paddingBottom: Spacing.two,
  },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },

  h1: {
    marginHorizontal: Spacing.four, marginTop: Spacing.three,
    fontFamily: Fonts.serif, fontSize: 22, color: Palette.ink, letterSpacing: -0.3,
  },
  sub: {
    marginHorizontal: Spacing.four, marginTop: 4,
    fontSize: 13, fontFamily: Fonts.sansMedium, color: Palette.inkSoft, lineHeight: 19,
  },

  progressCard: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: Spacing.four,
    marginTop: Spacing.four,
    paddingHorizontal: Spacing.four,
    paddingVertical: Spacing.three,
    backgroundColor: '#EAF0EA',
    borderRadius: Radius.lg,
    gap: Spacing.three,
  },
  progressStat: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  progressDivider: {
    width: 1,
    alignSelf: 'stretch',
    backgroundColor: '#CFDBCF',
  },
  progressNum: {
    fontFamily: Fonts.serifBold,
    fontSize: 22,
    color: Palette.forest,
    lineHeight: 26,
  },
  progressLabel: {
    fontFamily: Fonts.sansSemibold,
    fontSize: 13,
    color: Palette.inkSoft,
    marginTop: 2,
  },
  editBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: Palette.forest,
    backgroundColor: '#FFFFFF',
  },
  editBtnText: {
    fontFamily: Fonts.sansBold,
    fontSize: 12.5,
    color: Palette.forest,
    letterSpacing: 0.2,
  },
  editHint: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  editHintText: {
    fontFamily: Fonts.sansSemibold,
    fontSize: 11.5,
    color: Palette.inkFaint,
    letterSpacing: 0.3,
  },

  sectionTitle: {
    marginHorizontal: Spacing.four,
    marginTop: Spacing.four,
    marginBottom: 8,
    fontFamily: Fonts.sansBold,
    fontSize: 12,
    color: Palette.inkFaint,
    letterSpacing: 0.7,
  },
  list: {
    marginHorizontal: Spacing.four,
    gap: Spacing.two,
  },
  row: {
    flexDirection: 'row',
    gap: 10,
    backgroundColor: Palette.card,
    borderWidth: 1.5,
    borderColor: Palette.line,
    borderRadius: Radius.lg,
    padding: 10,
  },
  rowCompact: {
    paddingVertical: 8,
    alignItems: 'center',
  },
  rowFilled: {
    borderColor: Palette.forest,
    backgroundColor: '#F4F7F2',
  },
  iconBubble: {
    width: 32, height: 32, borderRadius: 10,
    alignItems: 'center', justifyContent: 'center',
  },
  photo: {
    width: 56, height: 56, borderRadius: 8,
    backgroundColor: Palette.sandDeep,
  },
  photoEmpty: {
    alignItems: 'center',
    justifyContent: 'center',
    borderStyle: 'dashed',
    borderWidth: 1,
    borderColor: Palette.line,
  },
  photoEmptyText: {
    fontFamily: Fonts.sansBold,
    fontSize: 16,
    color: Palette.inkFaint,
  },
  rowLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 13.5,
    color: Palette.ink,
  },
  rowWhere: {
    marginTop: 3,
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.ink,
    lineHeight: 16,
  },
  rowNote: {
    marginTop: 2,
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.inkSoft,
    lineHeight: 16,
  },
  rowFieldLabel: {
    fontFamily: Fonts.sansBold,
    color: Palette.forest,
  },
  rowNoteEmpty: {
    color: Palette.inkFaint,
    fontStyle: 'italic',
  },

  contactRow: {
    flexDirection: 'row',
    gap: 12,
    backgroundColor: Palette.card,
    borderWidth: 1.5,
    borderColor: Palette.line,
    borderRadius: Radius.lg,
    paddingHorizontal: 12,
    paddingVertical: 10,
    alignItems: 'center',
  },
  contactRole: {
    fontFamily: Fonts.sansBold,
    fontSize: 13.5,
    color: Palette.forest,
    width: 80,
  },
  contactName: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.ink,
  },
  contactPhone: {
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.inkSoft,
  },

  footer: {
    paddingHorizontal: Spacing.four,
    paddingBottom: Spacing.four,
    paddingTop: Spacing.two,
    borderTopWidth: 1,
    borderTopColor: Palette.line,
    backgroundColor: Palette.sand,
  },
  exportBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    borderRadius: Radius.lg,
    backgroundColor: Palette.forest,
  },
  exportBtnText: {
    fontFamily: Fonts.sansBold,
    fontSize: 14,
    color: Palette.cream,
    letterSpacing: 0.3,
  },

  // ---- Edit modal --------------------------------------------------------
  // Backdrop is an absolute-fill black layer; the Pressable inside it
  // closes the modal on tap. The dialog lives in a separate, centered
  // overlay so the two layers can animate independently (fade vs slide).
  modalBackdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: '#000',
  },
  modalCenter: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    padding: Spacing.four,
  },
  modalCard: {
    width: '100%',
    maxWidth: 440,
    backgroundColor: Palette.card,
    borderRadius: Radius.xl,
    padding: Spacing.four,
  },
  modalTitle: {
    fontFamily: Fonts.serifBold,
    fontSize: 18,
    color: Palette.ink,
    letterSpacing: -0.3,
  },
  modalHint: {
    marginTop: 6,
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.inkSoft,
    lineHeight: 17,
  },
  photoPickerWrap: {
    marginTop: Spacing.three,
    position: 'relative',
  },
  photoPicker: {
    width: '100%',
    height: 180,
    borderRadius: Radius.lg,
    overflow: 'hidden',
    backgroundColor: Palette.sand,
    borderWidth: 1,
    borderColor: Palette.line,
  },
  photoPreview: {
    width: '100%',
    height: '100%',
  },
  photoEmptyPicker: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  photoClearBtn: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: 'rgba(0,0,0,0.65)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  photoReplaceBtn: {
    position: 'absolute',
    bottom: 8,
    right: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: 'rgba(0,0,0,0.6)',
  },
  photoReplaceText: {
    fontFamily: Fonts.sansBold,
    fontSize: 12,
    color: Palette.cream,
    letterSpacing: 0.2,
  },
  fieldLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    marginTop: Spacing.three,
  },
  fieldLabel: {
    marginTop: Spacing.three,
    fontFamily: Fonts.sansBold,
    fontSize: 12,
    color: Palette.inkFaint,
    letterSpacing: 0.6,
  },
  modalInput: {
    marginTop: 6,
    minHeight: 70,
    borderWidth: 1.5,
    borderColor: Palette.line,
    borderRadius: Radius.md,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.ink,
    textAlignVertical: 'top',
  },
  modalBtnRow: {
    flexDirection: 'row',
    gap: Spacing.two,
    marginTop: Spacing.three,
  },
  cancelBtn: {
    flex: 1,
    paddingVertical: 13,
    borderRadius: 14,
    backgroundColor: Palette.sand,
    alignItems: 'center',
  },
  cancelBtnText: {
    fontFamily: Fonts.sansBold,
    fontSize: 14,
    color: Palette.inkSoft,
  },
  saveBtn: {
    flex: 2,
    paddingVertical: 13,
    borderRadius: 14,
    backgroundColor: Palette.forest,
    alignItems: 'center',
  },
  saveBtnText: {
    fontFamily: Fonts.sansBold,
    fontSize: 14,
    color: Palette.cream,
  },
});
